// Remote players: one procedural character rig per player, driven by interpolated snapshot state.

import * as THREE from "three";
import { createCharacter } from "./models/character";
import type { CharacterAnimInput, CharacterRig } from "./models/character";
import { createWeaponModel } from "./models/weapons";
import type { WeaponModel } from "./models/weapons";
import { PlayerFlag } from "./types";
import type {
  PlayerRenderer,
  RemotePlayerInfo,
  RemotePlayerRenderState,
  Vec3Like,
} from "./types";

/** Seconds after death when the body has fully faded out (the rig fades 2.2 s .. 3.0 s). */
const HIDE_AFTER_DEATH = 3.05;
const EYE_STAND = 1.62;
const EYE_CROUCH = 1.0;
const EYE_SLIDE = 0.85;
/** Nameplate anchor above the eyes. */
const HEAD_ABOVE_EYES = 0.4;
/** Weapon models kept per player: a loadout's primary, secondary and knife with a few variants. */
const MAX_CACHED_WEAPONS = 6;
const NO_ATTACHMENTS: readonly string[] = [];

export function newAnimInput(): CharacterAnimInput {
  return {
    velLocal: { x: 0, z: 0 },
    speed: 0,
    onGround: true,
    crouch: 0,
    sliding: false,
    wallrun: 0,
    mantling: false,
    ads: false,
    pitch: 0,
    firing: false,
    reloading: false,
    alive: true,
    deathTime: 0,
    shielded: false,
  };
}

/**
 * Maps a snapshot state to the rig's animation input. The rig faces -Z at yaw 0; yaw turns it
 * counter-clockwise (+Y), so the world velocity is rotated by -yaw into rig space
 * (+x = right, -z = forward).
 */
export function fillAnimInput(
  a: CharacterAnimInput,
  s: RemotePlayerRenderState,
): void {
  const f = s.flags;
  const sin = Math.sin(s.yaw);
  const cos = Math.cos(s.yaw);
  a.velLocal.x = s.vel.x * cos - s.vel.z * sin;
  a.velLocal.z = s.vel.x * sin + s.vel.z * cos;
  a.speed = Math.hypot(s.vel.x, s.vel.z);
  a.onGround = (f & PlayerFlag.ON_GROUND) !== 0;
  a.sliding = (f & PlayerFlag.SLIDING) !== 0;
  a.crouch = (f & (PlayerFlag.CROUCHED | PlayerFlag.SLIDING)) !== 0 ? 1 : 0;
  a.wallrun =
    (f & PlayerFlag.WALLRUN_LEFT) !== 0
      ? -1
      : (f & PlayerFlag.WALLRUN_RIGHT) !== 0
        ? 1
        : 0;
  a.mantling = (f & PlayerFlag.MANTLING) !== 0;
  a.ads = (f & PlayerFlag.ADS) !== 0;
  a.reloading = (f & PlayerFlag.RELOADING) !== 0;
  a.alive = (f & PlayerFlag.ALIVE) !== 0;
  a.firing = s.firing;
  a.pitch = s.pitch;
  a.deathTime = a.alive ? 0 : s.deathTime;
  a.shielded = (f & PlayerFlag.SPAWN_PROTECTED) !== 0;
}

/** Eye height of a pose (m above the feet), for nameplates. */
export function eyeHeight(flags: number): number {
  if ((flags & PlayerFlag.SLIDING) !== 0) return EYE_SLIDE;
  if ((flags & PlayerFlag.CROUCHED) !== 0) return EYE_CROUCH;
  return EYE_STAND;
}

interface Entry {
  info: RemotePlayerInfo;
  attachments: string[];
  rig: CharacterRig;
  anim: CharacterAnimInput;
  weapons: Map<string, WeaponModel>;
  weaponKey: string;
  head: Vec3Like;
  headValid: boolean;
}

export class PlayerManager implements PlayerRenderer {
  private readonly entries = new Map<number, Entry>();
  private readonly scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** Number of players currently in the scene (shadow refresh heuristic). */
  get count(): number {
    return this.entries.size;
  }

  upsert(info: RemotePlayerInfo): void {
    const existing = this.entries.get(info.id);
    if (existing !== undefined) {
      const recolor =
        existing.info.team !== info.team || existing.info.enemy !== info.enemy;
      if (recolor) {
        // Team colours are baked into the rig geometry: rebuild it in place.
        this.destroyRig(existing);
        existing.rig = this.createRig(info);
      }
      const loadoutChanged =
        existing.info.weaponId !== info.weaponId ||
        existing.info.camo !== info.camo ||
        existing.attachments.join(",") !== info.attachments.join(",");
      existing.info = { ...info };
      existing.attachments = info.attachments.slice();
      // The cached models stay (a weapon swap shows the same few again and again); only what is
      // shown is looked up anew, and a rebuilt rig needs its weapon back.
      if (loadoutChanged || recolor) existing.weaponKey = "\0";
      return;
    }
    const entry: Entry = {
      info: { ...info },
      attachments: info.attachments.slice(),
      rig: this.createRig(info),
      anim: newAnimInput(),
      weapons: new Map(),
      weaponKey: "\0",
      head: { x: 0, y: 0, z: 0 },
      headValid: false,
    };
    entry.rig.setVisible(false);
    this.entries.set(info.id, entry);
  }

  private createRig(info: RemotePlayerInfo): CharacterRig {
    const rig = createCharacter({ team: info.team, enemy: info.enemy });
    this.scene.add(rig.root);
    return rig;
  }

  private destroyRig(e: Entry): void {
    e.rig.setWeapon(null);
    e.rig.dispose();
  }

  setState(id: number, s: RemotePlayerRenderState, dt: number): void {
    const e = this.entries.get(id);
    if (e === undefined) return;
    this.syncWeapon(e, s.weaponId);
    fillAnimInput(e.anim, s);
    const hidden =
      s.hidden === true ||
      (e.anim.alive ? false : s.deathTime >= HIDE_AFTER_DEATH);
    e.rig.setVisible(!hidden);
    if (hidden) {
      e.headValid = false;
      return;
    }
    const root = e.rig.root;
    root.position.set(s.pos.x, s.pos.y, s.pos.z);
    root.rotation.y = s.yaw;
    e.rig.update(e.anim, dt);
    e.head.x = s.pos.x;
    e.head.y = s.pos.y + eyeHeight(s.flags) + HEAD_ABOVE_EYES;
    e.head.z = s.pos.z;
    e.headValid = e.anim.alive;
  }

  /** Shows the weapon the snapshot says the player holds (models are cached per player by weapon, attachments and camo). */
  private syncWeapon(e: Entry, weaponId: string): void {
    if (weaponId === e.weaponKey) return;
    e.weaponKey = weaponId;
    if (weaponId === "") {
      e.rig.setWeapon(null);
      return;
    }
    // Attachments and camo are only known for the weapon the render info names.
    const primary = weaponId === e.info.weaponId;
    const attachments = primary ? e.attachments : NO_ATTACHMENTS;
    const camo = primary ? e.info.camo : "none";
    const cacheKey = `${weaponId}|${attachments.join(",")}|${camo}`;
    let model = e.weapons.get(cacheKey);
    if (model === undefined) {
      try {
        model = createWeaponModel(weaponId, attachments.slice(), camo);
      } catch (err) {
        console.warn(`players: cannot build weapon "${weaponId}"`, err);
        e.rig.setWeapon(null);
        return;
      }
      model.root.traverse((o) => {
        if (o instanceof THREE.Mesh) o.castShadow = true;
      });
      e.weapons.set(cacheKey, model);
      this.trimWeapons(e, model);
    }
    e.rig.setWeapon(model.root);
  }

  /** Drops the oldest cached models beyond a few loadouts' worth; `keep` is on display. */
  private trimWeapons(e: Entry, keep: WeaponModel): void {
    for (const [key, w] of e.weapons) {
      if (e.weapons.size <= MAX_CACHED_WEAPONS) return;
      if (w === keep) continue;
      w.dispose();
      e.weapons.delete(key);
    }
  }

  remove(id: number): void {
    const e = this.entries.get(id);
    if (e === undefined) return;
    this.destroyEntry(e);
    this.entries.delete(id);
  }

  headPosition(id: number): Vec3Like | null {
    const e = this.entries.get(id);
    return e !== undefined && e.headValid ? e.head : null;
  }

  clear(): void {
    for (const e of this.entries.values()) this.destroyEntry(e);
    this.entries.clear();
  }

  private destroyEntry(e: Entry): void {
    this.destroyRig(e);
    for (const w of e.weapons.values()) w.dispose();
    e.weapons.clear();
  }
}
